/**
 * svgStats — outillage SVG partagé du dossier statistiques (5e, thème #6).
 *
 * Diagrammes STATIQUES d'énoncé ou de choix de QCM, générés par construction
 * (jamais d'images figées). Précédents repris : svgCamembert (gen_982),
 * imgElement/imgChoix (gen_925), styles de tableau (gen_985).
 *
 * Tout est paramétré par les DONNÉES du tirage : la figure est toujours
 * cohérente avec l'énoncé (source de vérité unique).
 */

// ─── Emballages payload ──────────────────────────────────────

/** Élément additionnel d'ÉNONCÉ (imageUrl en data-URI : correct ici, §12). */
export const imgElement = (svg, maxWidth = 'small') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
    } }],
});

/** CHOIX riche de QCM (règle 🔴 §1 : imageData obligatoire en choix). */
export const imgChoix = (svg) => ({
    type: 'image',
    config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` },
});

// ─── Palette (couleur primaire TanoEd en tête) ───────────────

export const COULEURS_STATS = ['#1E3A8A', '#b45309', '#0e7490', '#475569', '#15803d', '#9d174d'];

// ─── Tableaux HTML d'énoncé (idiome gen_985) ─────────────────

const T_TH = 'background:#1E3A8A;color:#fff;padding:5px 10px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:5px 10px;border:1px solid #64748b;text-align:center';

/** Ligne « en-tête à gauche + cellules » ; cells peut contenir « ? ». */
export const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';

/** Ligne d'en-tête COMPLÈTE (tableaux à double entrée : les entrées se
 * distinguent des cases de résultats). */
export const ligneTableEntete = (cells) =>
    `<tr>${cells.map((c) => `<th style="${T_TH}">${c}</th>`).join('')}</tr>`;

export const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

// ─── Diagramme en barres / bâtons ────────────────────────────

/**
 * svgBarres(categories, valeurs, opts) → string SVG.
 *
 * - `categories` : libellés de l'axe horizontal (strings courtes).
 * - `valeurs`    : nombres ≥ 0, même longueur (0 = pas de barre, la
 *                  catégorie reste étiquetée — piège « catégorie sans barre »).
 * - `opts.yStep` : pas des graduations horizontales (obligatoire).
 * - `opts.yMin`  : axe TRONQUÉ si > 0 (les hauteurs apparentes ne sont plus
 *                  proportionnelles — c'est le piège didactique de Paul,
 *                  Créé n°56). Les barres partent du bas du cadre.
 * - `opts.yMax`  : auto = plafond au multiple de yStep ≥ max(valeurs).
 * - `opts.yLabel` / `opts.xLabel` : noms des axes.
 * - `opts.batons` : true → bâtons fins (séries type « pointures »).
 * - `opts.compact` : true → gabarit réduit (choix de QCM).
 */
export const svgBarres = (categories, valeurs, opts) => {
    const { yStep, yMin = 0, yLabel = 'Effectifs', xLabel = '', batons = false, compact = false } = opts;
    const yMax = opts.yMax || Math.ceil(Math.max(...valeurs, 1) / yStep) * yStep;
    const n = categories.length;
    const colW = compact ? 34 : (n > 8 ? 40 : 56);
    const mL = compact ? 34 : 46;                       // marge gauche (labels y)
    // Le titre de l'axe des ordonnees s'ecrit AU-DESSUS du cadre, en haut a gauche.
    // Avec une marge haute de 12 px il se posait a la meme hauteur que la graduation
    // la plus haute, et les deux se chevauchaient — « Ventes (€) » par-dessus
    // « 500 » (retour prof 2026-09-28). La marge lui reserve maintenant sa ligne.
    const mT = yLabel ? 28 : 12;
    const plotH = compact ? 130 : 190;
    const fs = compact ? 10 : 12;
    // Libellés x : rotation quand ils sont trop larges pour la colonne
    // (sinon des mots comme « Clic-clac » ou « très souvent » se chevauchent).
    const labelPxMax = Math.max(...categories.map((c) => String(c).length)) * fs * 0.55;
    const rotateX = labelPxMax > colW - 2;
    const mB = rotateX
        ? Math.round(labelPxMax * 0.62) + (xLabel ? 22 : 12)
        : (compact ? 26 : (xLabel ? 44 : 30));          // marge basse
    const W = mL + n * colW + 14;
    const H = mT + plotH + mB;
    const yPix = (v) => mT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

    // Grille + labels y (toutes les graduations, sauf si > 12 → une sur deux)
    const nGrad = Math.round((yMax - yMin) / yStep);
    const labelEvery = nGrad > 12 ? 2 : 1;
    let grille = '';
    for (let i = 0; i <= nGrad; i++) {
        const v = yMin + i * yStep;
        const y = yPix(v).toFixed(1);
        grille += `<line x1="${mL}" y1="${y}" x2="${W - 8}" y2="${y}" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3,3"/>`;
        if (i % labelEvery === 0) {
            grille += `<text x="${mL - 5}" y="${+y + 4}" font-size="${fs}" fill="#334155" text-anchor="end">${v}</text>`;
        }
    }

    // Barres (hauteur clampée à 0 : une valeur ≤ yMin ne dépasse pas de l'axe)
    const barW = batons ? Math.min(16, colW * 0.3) : colW * 0.58;
    let barres = '';
    categories.forEach((cat, i) => {
        const cx = mL + i * colW + colW / 2;
        const h = Math.max(0, (Math.min(valeurs[i], yMax) - yMin)) / (yMax - yMin) * plotH;
        if (h > 0) {
            barres += `<rect x="${(cx - barW / 2).toFixed(1)}" y="${(mT + plotH - h).toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" fill="#1E3A8A" fill-opacity="0.75" stroke="#1E3A8A" stroke-width="1.5"/>`;
        }
        const ly = mT + plotH + fs + 4;
        barres += rotateX
            ? `<text x="${cx}" y="${ly}" font-size="${fs}" fill="#1e293b" text-anchor="end" transform="rotate(-32 ${cx} ${ly})">${cat}</text>`
            : `<text x="${cx}" y="${ly}" font-size="${fs}" fill="#1e293b" text-anchor="middle">${cat}</text>`;
    });

    const axes = `<line x1="${mL}" y1="${mT}" x2="${mL}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`
        + `<line x1="${mL}" y1="${mT + plotH}" x2="${W - 8}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`;
    const labels = `<text x="12" y="${mT - 12}" font-size="${fs}" fill="#334155">${yLabel}</text>`
        + (xLabel ? `<text x="${mL + (W - mL) / 2}" y="${H - 6}" font-size="${fs}" fill="#334155" text-anchor="middle">${xLabel}</text>` : '');

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>${grille}${axes}${barres}${labels}</svg>`;
};

// ─── Diagramme circulaire ────────────────────────────────────

/**
 * svgCamembert(parts, opts) → string SVG.
 * `parts` : [{ nom, pct, etiquette? }] — pct sommant à 100.
 * `opts.titre` / `opts.sousTitre` : au-dessus du disque (« ville Alpha », « 2 000 licenciés »).
 * `opts.labelsSurSecteurs` : true → le % s'affiche au milieu de chaque secteur.
 * `opts.legende` : false → pas de légende à droite (choix de QCM compacts).
 * `opts.compact` : true → gabarit réduit.
 */
export const svgCamembert = (parts, opts = {}) => {
    const { titre = '', sousTitre = '', labelsSurSecteurs = false, legende = true, compact = false } = opts;
    const r = compact ? 62 : 88;
    const topPad = titre ? (sousTitre ? 44 : 26) : 8;
    const cx = r + 16, cy = topPad + r + 4;
    const W = legende ? cx + r + 200 : cx + r + 16;
    const H = cy + r + 10;
    let angle = -90;
    let secteurs = '';
    let labels = '';
    parts.forEach((p, i) => {
        const a0 = (angle * Math.PI) / 180;
        const mid = ((angle + (p.pct * 360) / 200) * Math.PI) / 180;
        angle += (p.pct * 360) / 100;
        const a1 = (angle * Math.PI) / 180;
        const large = p.pct > 50 ? 1 : 0;
        const x0 = (cx + r * Math.cos(a0)).toFixed(1), y0 = (cy + r * Math.sin(a0)).toFixed(1);
        const x1 = (cx + r * Math.cos(a1)).toFixed(1), y1 = (cy + r * Math.sin(a1)).toFixed(1);
        secteurs += `<path d="M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z" fill="${COULEURS_STATS[i % COULEURS_STATS.length]}" stroke="#fff" stroke-width="2"/>`;
        if (labelsSurSecteurs && p.pct >= 7) {
            const lx = (cx + 0.62 * r * Math.cos(mid)).toFixed(1), ly = (cy + 0.62 * r * Math.sin(mid) + 4).toFixed(1);
            labels += `<text x="${lx}" y="${ly}" font-size="${compact ? 11 : 13}" fill="#fff" font-weight="bold" text-anchor="middle">${p.pct} %</text>`;
        }
    });
    const titres = (titre ? `<text x="${cx}" y="16" font-size="14" fill="#1e293b" font-weight="bold" text-anchor="middle">${titre}</text>` : '')
        + (sousTitre ? `<text x="${cx}" y="34" font-size="13" fill="#334155" text-anchor="middle">${sousTitre}</text>` : '');
    const leg = legende ? parts.map((p, i) =>
        `<rect x="${cx + r + 22}" y="${topPad + 8 + i * 26}" width="14" height="14" fill="${COULEURS_STATS[i % COULEURS_STATS.length]}"/>`
        + `<text x="${cx + r + 42}" y="${topPad + 20 + i * 26}" font-size="13" fill="#1e293b">${p.nom}${p.etiquette ? ` : ${p.etiquette}` : ''}</text>`).join('') : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${titres}${secteurs}${labels}${leg}</svg>`;
};

// ─── Ligne brisée (points, reliés ou non) ────────────────────

/**
 * svgLigneBrisee(categories, valeurs, opts) → string SVG.
 * Points sur des abscisses régulières, reliés par des segments si
 * `opts.relier` (défaut true). Sert au débat « relier ou pas » (Créé n°58)
 * et aux graphiques d'évolution.
 */
export const svgLigneBrisee = (categories, valeurs, opts) => {
    const { yStep, yLabel = 'Effectifs', xLabel = '', relier = true } = opts;
    const yMax = opts.yMax || Math.ceil(Math.max(...valeurs, 1) / yStep) * yStep;
    const n = categories.length;
    // Meme marge haute que le diagramme en barres : le titre de l'axe y a sa ligne.
    const colW = 56, mL = 46, mB = xLabel ? 44 : 30, mT = yLabel ? 28 : 12, plotH = 180;
    const W = mL + n * colW + 14;
    const H = mT + plotH + mB;
    const yPix = (v) => mT + plotH - (v / yMax) * plotH;
    const xPix = (i) => mL + i * colW + colW / 2;

    const nGrad = Math.round(yMax / yStep);
    let grille = '';
    for (let i = 0; i <= nGrad; i++) {
        const v = i * yStep;
        const y = yPix(v).toFixed(1);
        grille += `<line x1="${mL}" y1="${y}" x2="${W - 8}" y2="${y}" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3,3"/>`
            + `<text x="${mL - 5}" y="${+y + 4}" font-size="12" fill="#334155" text-anchor="end">${v}</text>`;
    }
    const ligne = relier
        ? `<polyline points="${valeurs.map((v, i) => `${xPix(i).toFixed(1)},${yPix(v).toFixed(1)}`).join(' ')}" fill="none" stroke="#b45309" stroke-width="2"/>`
        : '';
    let points = '';
    categories.forEach((cat, i) => {
        points += `<circle cx="${xPix(i).toFixed(1)}" cy="${yPix(valeurs[i]).toFixed(1)}" r="4" fill="#1E3A8A"/>`
            + `<text x="${xPix(i)}" y="${mT + plotH + 16}" font-size="12" fill="#1e293b" text-anchor="middle">${cat}</text>`;
    });
    const axes = `<line x1="${mL}" y1="${mT}" x2="${mL}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`
        + `<line x1="${mL}" y1="${mT + plotH}" x2="${W - 8}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`;
    const labels = `<text x="12" y="${mT - 12}" font-size="12" fill="#334155">${yLabel}</text>`
        + (xLabel ? `<text x="${mL + (W - mL) / 2}" y="${H - 6}" font-size="12" fill="#334155" text-anchor="middle">${xLabel}</text>` : '');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>${grille}${axes}${ligne}${points}${labels}</svg>`;
};
